import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@provenleap/ui";
import { AuthLayout } from "../layout/AuthLayout";

const STORAGE_KEY = "waitlist_email";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

function validateEmail(value: string): string | undefined {
  if (!value) return "Email is required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
  return undefined;
}

export function WaitlistPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(
    () => !!localStorage.getItem(STORAGE_KEY),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      const response = await fetch(`${apiBaseUrl}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.ok || response.status === 409) {
        // 409 = already on list, still show success
        localStorage.setItem(STORAGE_KEY, email);
        setSubmitted(true);
      } else {
        const data = await response.json().catch(() => null);
        if (data?.errors?.[0]?.message) {
          setError(data.errors[0].message);
        } else {
          setError("Something went wrong. Please try again.");
        }
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout>
        <Card className="w-full !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">You're on the list!</CardTitle>
            <CardDescription>
              We'll notify you as soon as ProvenLeap is ready. Stay tuned!
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link
              to="/"
              className="text-sm font-medium text-primary hover:underline flex items-center justify-center gap-1"
            >
              &larr; Back to homepage
            </Link>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="w-full !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
        <CardHeader className="pb-6">
          <CardTitle className="mb-4">Join the Waitlist</CardTitle>
          <CardDescription className="text-foreground/85">
            Be the first to know when ProvenLeap launches. Enter your email
            below and we'll keep you in the loop.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                className="rounded-xl"
                placeholder="you@agency.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(undefined);
                }}
                onBlur={() => {
                  if (email) setError(validateEmail(email));
                }}
                aria-invalid={!!error}
                aria-describedby={error ? "email-error" : undefined}
              />
              {error && (
                <p id="email-error" className="mt-1.5 text-sm text-red-500">
                  {error}
                </p>
              )}
            </div>
            <Button type="submit" variant="brand" className="w-full" disabled={loading}>
              {loading ? "Joining..." : "Join Waitlist"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link
              to="/"
              className="text-sm font-medium text-primary hover:underline flex items-center justify-center gap-1"
            >
              &larr; Back to homepage
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
